import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Mail, Search, Trash2, Users } from "lucide-react";
import { api, errMsg, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const LEVELS = [["view", "Lecture seule"], ["edit", "Modification"]];
const LEVEL_HELP = { view: "peut ouvrir, lire et télécharger", edit: "peut aussi déposer, renommer, déplacer et supprimer" };

function LevelSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-40 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{LEVELS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function PeoplePicker({ people, perms, onAdd }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [level, setLevel] = useState("view");
  const has = (type, id) => perms.some((p) => p.type === type && p.id === id);
  const options = useMemo(() => [
    ...["responsable", "salarie", "externe"].map((r) => ({ key: `role:${r}`, label: `Tous les ${ROLE_LABELS[r]}s`, sub: "Groupe", group: true })),
    ...people.map((p) => ({ key: `user:${p.user_id}`, label: p.name || p.email, sub: `${p.email} · ${ROLE_LABELS[p.role] || p.role}` })),
  ].filter((o) => !has(...o.key.split(":")) && `${o.label} ${o.sub}`.toLowerCase().includes(q.toLowerCase())), [people, perms, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) => setPicked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const add = () => {
    onAdd([...picked].map((k) => { const o = options.find((x) => x.key === k); const [type, id] = k.split(":"); return { type, id, level, name: o?.label }; }));
    setPicked(new Set()); setQ("");
  };
  return (
    <div className="border rounded-lg" data-testid="share-picker">
      <div className="relative border-b">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un collaborateur…" className="border-0 pl-9 focus-visible:ring-0" data-testid="share-search-input" />
      </div>
      <div className="max-h-44 overflow-auto divide-y">
        {options.length === 0 && <p className="text-xs text-slate-500 p-3">Aucun collaborateur à ajouter.</p>}
        {options.map((o) => (
          <label key={o.key} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`share-option-${o.key.replace(":", "-")}`}>
            <Checkbox checked={picked.has(o.key)} onCheckedChange={() => toggle(o.key)} />
            {o.group && <Users className="h-4 w-4 text-[#1E3A8A]" />}
            <span className="min-w-0"><span className="block truncate font-medium">{o.label}</span><span className="block truncate text-xs text-slate-500">{o.sub}</span></span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 p-2 border-t bg-slate-50">
        <LevelSelect value={level} onChange={setLevel} testid="share-new-level" />
        <span className="text-xs text-slate-500 flex-1 hidden sm:block">{LEVEL_HELP[level]}</span>
        <Button size="sm" onClick={add} disabled={!picked.size} data-testid="share-add-btn"><Check className="h-4 w-4 mr-1" />Ajouter {picked.size || ""}</Button>
      </div>
    </div>
  );
}

export function PermissionsDialog({ open, onOpenChange, item, kind, onSaved }) {
  const [perms, setPerms] = useState([]);
  const [people, setPeople] = useState([]);
  const [mail, setMail] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setPerms(item.permissions || []); setMessage("");
    api.get("/directory").then((r) => setPeople(r.data.filter((p) => p.user_id !== item.owner_id)));
  }, [open, item]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/${kind}/${item.id}/permissions`, { permissions: perms, notify_email: mail, message: message || null });
      toast.success(data.notified ? `Partagé avec ${data.notified} nouvelle(s) personne(s)${data.mailed ? ", email envoyé" : ""}` : "Autorisations enregistrées");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle>Partager « {item?.name} »</DialogTitle>
          <DialogDescription>Cochez les collaborateurs, choisissez leur droit puis enregistrez.{kind === "folders" ? " Les droits s'appliquent à tout le contenu du dossier." : ""}</DialogDescription>
        </DialogHeader>
        <PeoplePicker people={people} perms={perms} onAdd={(list) => setPerms((p) => [...p, ...list])} />
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Personnes ayant accès</div>
          <div className="border rounded-lg divide-y max-h-56 overflow-auto" data-testid="permissions-list">
            {perms.length === 0 && <p className="text-sm text-slate-500 p-3">Accès limité au propriétaire et aux administrateurs.</p>}
            {perms.map((p) => (
              <div key={`${p.type}:${p.id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`permission-row-${p.id}`}>
                <span className="truncate flex items-center gap-2">{p.type === "role" && <Users className="h-4 w-4 text-[#1E3A8A]" />}{p.name || p.id}</span>
                <div className="flex items-center gap-1">
                  <LevelSelect value={p.level} onChange={(v) => setPerms((x) => x.map((y) => (y === p ? { ...y, level: v } : y)))} testid={`permission-level-${p.id}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Retirer l'accès"
                    onClick={() => setPerms((x) => x.filter((y) => y !== p))} data-testid={`permissions-remove-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="share-notify-label">
          <Checkbox checked={mail} onCheckedChange={(v) => setMail(!!v)} data-testid="share-notify-checkbox" /><Mail className="h-4 w-4 text-slate-500" />Prévenir les nouvelles personnes par email
        </label>
        {mail && <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={2} placeholder="Message (facultatif)" data-testid="share-message-input" />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="permissions-save-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
